import { Injectable, OnDestroy } from '@angular/core';
import * as PROMPTS from "./prompt.constant";
import { BehaviorSubject, Subject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { AskGenAiService } from 'src/app/askGenAi.service';

const mapDays = {
    "1": "first",
    "2": "second",
    "3": "third",
    "4": "fourth",
    "5": "fifth",
};

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const removeSentenceFromParagraph = (paragraph, sentenceToRemove) => {
    const regex = new RegExp(escapeRegExp(sentenceToRemove), 'g');
    return paragraph.replace(regex, '');
}

@Injectable({
  providedIn: 'root',
})
export class PlanitService implements OnDestroy {
    private isDestroyed: boolean = false;
    private _agendaTime: string = '';
    private _subscription_id: string = '';
    private finalResponse = {};
    
    public endTime: string[] = [];
    public showError: boolean = false;
    public planitLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public planitResponse: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    
    palmResponse: Subject<string> = new Subject<string>();
    activitiesResponse: Subject<string> = new Subject<string>();
    result = [];
    activities = [];

    constructor(
        private askGenAi: AskGenAiService
    ) {}

    get agendaTime(): string {
        return this._agendaTime;
    }
    set agendaTime(time: string) {
        this.calculateEndtime(time)
        this._agendaTime = time;
    }
    
    get subscription_id(): string {
        return this._subscription_id;
    }
    set subscription_id(id: string) {
        this._subscription_id = id;
    }

    calculateEndtime(time: string) {
        const currentTime = time.substring(time.indexOf("o ")+2, time.length-2);
        let adjustedTime = currentTime.substring(0, currentTime.indexOf(":"));
        if (parseInt(adjustedTime) < 10) {
            adjustedTime = String(parseInt(adjustedTime) + 12);
            this.endTime.push(adjustedTime);
        }
        this.endTime.push(currentTime.substring(0, currentTime.indexOf(":")));
    }

    calculateMultiPrompt(initialPrompt: string, day: number) {
        let promptInput = 'Given this output for following input:\n';

        // add initial prompt
        promptInput += initialPrompt;

        // add output
        if (day === 2) {
            promptInput += `\nYou got following output for day 1:\n`;
        } else {
            promptInput += `\nYou got following output for day 1 to day ${day-1}:\n`;
        }
        promptInput += JSON.stringify(this.result);
        
        promptInput += `\n\nNow, give the answer for the ${mapDays[day]} day. Make sure to keep the give timeframe. ${this.agendaTime}\n`;

        promptInput += PROMPTS.output2;
        
        promptInput += `\nMake sure that the output is in parsable by JSON! Put the keys in double quotation.\n`;

        return promptInput;
    }

    calculateActivitiesPrompt(day: number, initialPrompt: string, index: number) {
        let promptInput = PROMPTS.activitiesPrefix;
        
        promptInput += initialPrompt + '\n';

        promptInput += 'The result of the first agenda looks as follows:\n';

        promptInput += JSON.stringify(this.result[day-1][`${day}`]);
        
        promptInput += PROMPTS.activities;

        promptInput += PROMPTS.outputActivity;

        promptInput += '\nCome up with a list of activity that suits the topic and the title. The activity attribute must be a list of strings. Extend the following Object:';
        promptInput += JSON.stringify(this.result[day-1][`${day}`][index]);

        return promptInput;
    }

    preprocessingResponse(data: string) {
        let response = data;
        if (response.indexOf("```") !== -1) {
            response = response.substring(
                response.indexOf("```")+3,
                response.lastIndexOf("```")
            );
        } else {
            response = response.substring(
                response.indexOf("{"),
                response.lastIndexOf("}")+1
            );
        }
        if (response.toLowerCase().startsWith("json")) {
            response = response.substring(4);
        }
        return response;
    }

    async callPalm(prompt: string, cbSubject: Subject<string>, model: string = "text-bison") {
        await this.askGenAi
        .askGenAi(this.subscription_id, prompt, undefined, model, undefined)
            .subscribe((data) => {
                if (data.model_responses[0]) {
                    console.log(data.model_responses[0]);
                    cbSubject.next(data.model_responses[0])
                }
            });
    }

    updateResultsWithActivities(day: number) {
        let row = [];
        this.result[day-1][`${day}`].forEach(value => {
            const index = this.activities.findIndex(item => item.time === value.time);

            if (index !== -1) {
                row.push({...value, activities: this.activities[index].activities});
            } else {
                row.push({...value, activities: []});
            }
        })

        this.finalResponse[`${day}`] = row;
    }

    resetState() {
        this.isDestroyed = false;
        this.palmResponse = new Subject<string>();
        this.activitiesResponse = new Subject<string>();
        this.result = [];
        this.activities = [];
        this.showError = false;
        this.planitLoading.next(false);
        this.finalResponse = {};
    }

    async delayedLoop(day: number, modifiedInitialPrompt: string, delay: number) {
        const activities = this.result[day - 1][`${day}`];
        for (let i = 0; i < activities.length; i++) {
            const prompt = this.calculateActivitiesPrompt(day, modifiedInitialPrompt, i);
            await new Promise(resolve => setTimeout(resolve, i * delay));
            this.callPalm(prompt, this.activitiesResponse);
        }
    }

    //handle prompts (single and multiprompts)
    async generateWorkshop(topic: string, days: number, initialPrompt: string) {
        this.resetState();
        this.planitLoading.next(true);
        let day = 1;
        let objectIndex = 1;
        this.palmResponse.pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((data) => {
                let response = '';
                response = this.preprocessingResponse(data);
                console.log(response)
                try {
                    const task = JSON.parse(response);
                    this.result.push(task);
                } catch (error) {
                    console.error(error);
                    this.showError = true;
                }

                if (!this.showError) {
                    if (day < days) {
                        day++;
                        const prompt = this.calculateMultiPrompt(initialPrompt, day);
                        this.callPalm(prompt, this.palmResponse);
                    } else {
                        console.log(this.result)
                        day = 1;
                        //activities
                        // go one by one 
                        const modifiedInitialPrompt = removeSentenceFromParagraph(initialPrompt, 'Only give the first day!\n');
                        this.delayedLoop(day, modifiedInitialPrompt, 400);
                    }
                }
            });
        
        this.activitiesResponse.pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((data) => {
                let response = '';
                response = this.preprocessingResponse(data);
                try {
                    const task = JSON.parse(response);
                    this.activities.push(task);
                } catch (error) {
                    console.error(error, response);
                    this.activities.push([]);
                }
                console.log(objectIndex, this.result[day-1][`${day}`].length)
                if (objectIndex < this.result[day-1][`${day}`].length) {
                    objectIndex++;
                } else {
                    objectIndex = 1;
                    this.updateResultsWithActivities(day);
                    this.activities = [];

                    if (day < days) {
                        day++;
                        const modifiedInitialPrompt = removeSentenceFromParagraph(initialPrompt, 'Only give the first day!\n');
                        this.delayedLoop(day, modifiedInitialPrompt, 400);
                    } else {
                        console.log(this.finalResponse);
                        this.planitResponse.next(this.finalResponse);
                        day = 1;
                        objectIndex = 1;
                        this.planitLoading.next(false);
                        this.isDestroyed = true;
                    }
                }
            })

        await this.callPalm(initialPrompt, this.palmResponse);
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

}